/**
 * Notification Settings Panel Component
 *
 * Provides configuration options for onboarding form notifications including:
 * - Email notifications with recipient list
 * - WhatsApp notifications with phone numbers
 * - Dashboard-only mode
 */

import { useState } from 'react';
import {
  Bell,
  Mail,
  MessageSquare,
  Plus,
  X,
  LayoutDashboard,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { NotificationConfig, defaultNotificationConfig } from '@/lib/forms/templates';

interface NotificationSettingsPanelProps {
  config: NotificationConfig;
  onChange: (config: NotificationConfig) => void;
  className?: string;
}

export function NotificationSettingsPanel({
  config,
  onChange,
  className,
}: NotificationSettingsPanelProps) {
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');

  const updateConfig = (updates: Partial<NotificationConfig>) => {
    onChange({ ...config, ...updates });
  };

  const addEmail = () => {
    if (newEmail && !config.emailRecipients.includes(newEmail)) {
      updateConfig({
        emailRecipients: [...config.emailRecipients, newEmail],
      });
      setNewEmail('');
    }
  };

  const removeEmail = (email: string) => {
    updateConfig({
      emailRecipients: config.emailRecipients.filter((e) => e !== email),
    });
  };

  const addPhone = () => {
    if (newPhone && !config.whatsappNumbers.includes(newPhone)) {
      updateConfig({
        whatsappNumbers: [...config.whatsappNumbers, newPhone],
      });
      setNewPhone('');
    }
  };

  const removePhone = (phone: string) => {
    updateConfig({
      whatsappNumbers: config.whatsappNumbers.filter((p) => p !== phone),
    });
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <Bell className="h-4 w-4 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <CardTitle className="text-base">Notification Settings</CardTitle>
            <CardDescription className="text-xs">
              Get notified when someone submits this form
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Notify on Submission */}
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="notify-submission" className="text-sm font-medium">
              Notify on submission
            </Label>
            <p className="text-xs text-muted-foreground">
              Send notifications when form is submitted
            </p>
          </div>
          <Switch
            id="notify-submission"
            checked={config.notifyOnSubmission}
            onCheckedChange={(checked) =>
              updateConfig({ notifyOnSubmission: checked })
            }
          />
        </div>

        <Separator />

        {/* Email Notifications */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Email Notifications</Label>
            </div>
            <Switch
              checked={config.emailEnabled}
              onCheckedChange={(checked) =>
                updateConfig({ emailEnabled: checked })
              }
            />
          </div>

          {config.emailEnabled && (
            <div className="space-y-3 pl-6">
              {/* Email Recipients */}
              <div className="flex flex-wrap gap-2">
                {config.emailRecipients.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {email}
                    <button
                      onClick={() => removeEmail(email)}
                      className="ml-1 rounded-full hover:bg-muted p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {/* Add Email */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addEmail()}
                  placeholder="Enter email address"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEmail}
                  disabled={!newEmail}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {config.emailRecipients.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add email addresses to receive notifications
                </p>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* WhatsApp Notifications */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">WhatsApp Notifications</Label>
            </div>
            <Switch
              checked={config.whatsappEnabled}
              onCheckedChange={(checked) =>
                updateConfig({ whatsappEnabled: checked })
              }
            />
          </div>

          {config.whatsappEnabled && (
            <div className="space-y-3 pl-6">
              {/* Phone Numbers */}
              <div className="flex flex-wrap gap-2">
                {config.whatsappNumbers.map((phone) => (
                  <Badge
                    key={phone}
                    variant="secondary"
                    className="gap-1 pr-1"
                  >
                    {phone}
                    <button
                      onClick={() => removePhone(phone)}
                      className="ml-1 rounded-full hover:bg-muted p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {/* Add Phone */}
              <div className="flex gap-2">
                <Input
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addPhone()}
                  placeholder="Enter phone number (with country code)"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPhone}
                  disabled={!newPhone}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {config.whatsappNumbers.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add phone numbers to receive WhatsApp notifications
                </p>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* Dashboard Only Mode */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
            <div>
              <Label htmlFor="dashboard-only" className="text-sm font-medium">
                Dashboard only
              </Label>
              <p className="text-xs text-muted-foreground">
                Only show submissions in dashboard, no notifications
              </p>
            </div>
          </div>
          <Switch
            id="dashboard-only"
            checked={config.dashboardOnly}
            onCheckedChange={(checked) =>
              updateConfig({
                dashboardOnly: checked,
                emailEnabled: checked ? false : config.emailEnabled,
                whatsappEnabled: checked ? false : config.whatsappEnabled,
              })
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Compact Notification Settings for the form builder sidebar
 */
interface NotificationSettingsCompactProps {
  config: NotificationConfig;
  onChange: (config: NotificationConfig) => void;
}

export function NotificationSettingsCompact({
  config,
  onChange,
}: NotificationSettingsCompactProps) {
  const updateConfig = (updates: Partial<NotificationConfig>) => {
    onChange({ ...config, ...updates });
  };

  const totalRecipients =
    config.emailRecipients.length + config.whatsappNumbers.length;

  return (
    <div className="space-y-4 p-4 rounded-lg border">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Bell className="h-4 w-4 text-green-500" />
        Notifications
      </div>

      <div className="space-y-3">
        {/* Email */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">Email</span>
            {config.emailRecipients.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {config.emailRecipients.length}
              </Badge>
            )}
          </div>
          <Switch
            checked={config.emailEnabled}
            onCheckedChange={(checked) =>
              updateConfig({ emailEnabled: checked })
            }
          />
        </div>

        {/* WhatsApp */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">WhatsApp</span>
            {config.whatsappNumbers.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {config.whatsappNumbers.length}
              </Badge>
            )}
          </div>
          <Switch
            checked={config.whatsappEnabled}
            onCheckedChange={(checked) =>
              updateConfig({ whatsappEnabled: checked })
            }
          />
        </div>

        {/* Dashboard Only */}
        <div className="flex items-center justify-between">
          <span className="text-sm">Dashboard only</span>
          <Switch
            checked={config.dashboardOnly}
            onCheckedChange={(checked) =>
              updateConfig({
                dashboardOnly: checked,
                emailEnabled: checked ? false : config.emailEnabled,
                whatsappEnabled: checked ? false : config.whatsappEnabled,
              })
            }
          />
        </div>
      </div>

      {totalRecipients === 0 && !config.dashboardOnly && (
        <p className="text-xs text-muted-foreground">
          No recipients configured
        </p>
      )}
    </div>
  );
}
